import { ArrowRight, RefreshCw, Store } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { MarketplaceListing } from '../../types/marketplace';
import { MarketplaceListingCard } from './MarketplaceListingCard';

export const MarketplaceGridSkeleton = ({ count = 8 }: { count?: number }) => (
  <div
    className="grid grid-cols-1 gap-4 min-[460px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
    aria-label="Loading marketplace listings"
    role="status"
  >
    {Array.from({ length: count }).map((_, index) => (
      <div
        key={index}
        className="overflow-hidden rounded-lg border border-black/8 bg-white dark:border-white/10 dark:bg-[#111]"
      >
        <div className="aspect-[4/3] animate-pulse bg-neutral-200 dark:bg-white/[0.06]" />
        <div className="space-y-3 p-5">
          <div className="h-3 w-24 animate-pulse rounded bg-neutral-200 dark:bg-white/10" />
          <div className="h-5 w-4/5 animate-pulse rounded bg-neutral-200 dark:bg-white/10" />
          <div className="h-4 w-full animate-pulse rounded bg-neutral-100 dark:bg-white/[0.06]" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-neutral-100 dark:bg-white/[0.06]" />
        </div>
      </div>
    ))}
  </div>
);

export const MarketplaceErrorState = ({
  onRetry,
  message = 'The marketplace could not be loaded right now.',
}: {
  onRetry: () => void;
  message?: string;
}) => (
  <div
    className="rounded-lg border border-red-500/20 bg-red-500/[0.06] px-6 py-12 text-center"
    role="alert"
  >
    <h3 className="text-xl font-semibold text-neutral-950 dark:text-white">
      Marketplace temporarily unavailable
    </h3>
    <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-neutral-600 dark:text-white/55">
      {message}
    </p>
    <button
      type="button"
      onClick={onRetry}
      className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-xl bg-neutral-950 px-5 text-sm font-semibold text-white outline-none hover:bg-neutral-800 focus-visible:ring-2 focus-visible:ring-indigo-500 dark:bg-white dark:text-black dark:hover:bg-neutral-200"
    >
      <RefreshCw className="h-4 w-4" />
      Try again
    </button>
  </div>
);

export const MarketplaceEmptyState = ({
  title = 'New listings are being added',
  description = 'There are no published offers in this section yet. Check back soon or start selling on Wersee.',
  clearAction,
}: {
  title?: string;
  description?: string;
  clearAction?: () => void;
}) => (
  <div className="rounded-lg border border-dashed border-black/15 bg-black/[0.02] px-6 py-14 text-center dark:border-white/15 dark:bg-white/[0.025]">
    <Store className="mx-auto h-8 w-8 text-indigo-500 dark:text-indigo-300" />
    <h3 className="mt-4 text-xl font-semibold text-neutral-950 dark:text-white">
      {title}
    </h3>
    <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-neutral-600 dark:text-white/55">
      {description}
    </p>
    <div className="mt-6 flex flex-wrap justify-center gap-3">
      {clearAction && (
        <button
          type="button"
          onClick={clearAction}
          className="inline-flex min-h-11 items-center rounded-xl border border-black/10 px-4 text-sm font-semibold text-neutral-800 hover:bg-black/5 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
        >
          Clear filters
        </button>
      )}
      <Link
        to="/sell"
        className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-neutral-950 px-5 text-sm font-semibold text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-neutral-200"
      >
        Sell on Wersee <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  </div>
);

export const MarketplaceListingGrid = ({
  listings,
  eagerFirstImage = false,
}: {
  listings: MarketplaceListing[];
  eagerFirstImage?: boolean;
}) => (
  <div className="grid grid-cols-1 gap-4 min-[460px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
    {listings.map((listing, index) => (
      <MarketplaceListingCard
        key={listing.id}
        listing={listing}
        eagerImage={eagerFirstImage && index === 0}
      />
    ))}
  </div>
);
